/**
 * A live week (`media: { kind: 'week', collection }` on a section): the next few days of a collection's
 * real events in a card that looks like someone's own calendar, the events arriving in it one by one, as
 * a synced calendar fills itself. Drawn from the site's own rows as the page loads, so it shows what is
 * actually on (siteModel.weekArt decides which days, hours and events).
 *
 * The motion follows the brand rules (.claude/skills/brand-motion): one clock that every timing is a
 * fraction of; one event arriving at a time, each landing before the next sets out; the same on every
 * play; and for anyone who asks for less motion, the end state — the full week — standing still.
 */
import { useMemo } from 'react';
import { weekArt } from './siteModel.js';

const W = 560, H = 430;
const CARD = { x: 18, y: 18, w: W - 36, h: H - 36, r: 18 };
const GUTTER = 64;
const TOP = 138;
const BOTTOM = CARD.y + CARD.h - 22;
// One clock: when the first event sets out, the gap between arrivals, how long a landing takes, and how
// long the full week holds before it clears and begins again.
const FIRST = 0.6, STEP = 1.05, LAND = 0.45, HOLD = 2.8, CLEAR = 0.5, REST = 0.6;

const hourLabel = (h) => (h === 0 || h === 24 ? '12 AM' : h === 12 ? '12 PM' : h > 12 ? `${h - 12} PM` : `${h} AM`);
const clock = (x) => {
  const h = Math.floor(x); const m = Math.round((x - h) * 60);
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}${m ? `:${String(m).padStart(2, '0')}` : ''}`;
};
// How wide a run of 12px bold text is, near enough, from the averages measured in Inter on the site
// (2026-10-13): capitals 8.5, figures 7.6, lower case 6.9, punctuation 5.8, a space 2.6. A count of
// characters alone let an all-capitals title run out of its block.
const textWidth = (s) => [...String(s)].reduce((w, ch) => w
  + (/[A-Z]/.test(ch) ? 8.5 : /[0-9]/.test(ch) ? 7.6 : /[a-z]/.test(ch) ? 6.9 : ch === ' ' ? 2.6 : 5.8), 0);
/** `s` cut to fit `room` units, with an ellipsis when it had to be. */
function cut(s, room) {
  const t = String(s);
  if (textWidth(t) <= room) return t;
  let k = t.length;
  while (k > 1 && textWidth(`${t.slice(0, k).trimEnd()}…`) > room) k--;
  return `${t.slice(0, k).trimEnd()}…`;
}
/** `s` in at most `lines` lines that fit `room`, broken between words, the last cut on a word if it must be. */
function wrap(s, room, lines) {
  const words = String(s).split(/\s+/).filter(Boolean);
  const out = [];
  let cur = '';
  for (const w of words) {
    if (!cur) cur = w;
    else if (textWidth(`${cur} ${w}`) <= room) cur += ` ${w}`;
    else { out.push(cur); cur = w; if (out.length === lines) break; }
  }
  if (out.length < lines && cur) out.push(cur);
  if (out.join(' ').length < String(s).trim().length) {
    let last = out[out.length - 1];
    while (textWidth(`${last}…`) > room && last.includes(' ')) last = last.slice(0, last.lastIndexOf(' '));
    out[out.length - 1] = textWidth(`${last}…`) <= room ? `${last}…` : cut(last, room);
  }
  return out.map((l) => cut(l, room));
}

export default function SiteWeekArt({ rows, ctx, id, label = 'Your calendar', from = '', field = null }) {
  // `field`: the one whose first word each event shows under its time (a dance style).
  const week = useMemo(() => weekArt(rows, { rowsById: ctx.rowsById, styleField: field }), [rows, ctx.rowsById, field]);
  if (!week.events.length) return null;
  const { days, hours, events } = week;
  const col = (CARD.w - GUTTER - 18) / days.length;
  const colX = (d) => CARD.x + GUTTER + d * col;
  const row = (BOTTOM - TOP) / (hours.length - 1);
  const hourY = (h) => TOP + (h - hours[0]) * row;
  const room = col - 8 - 17;   // a block's width less its bar and padding, in the units textWidth counts
  const period = FIRST + events.length * STEP + HOLD + CLEAR + REST;
  const pct = (s) => +((s / period) * 100).toFixed(2);
  const ns = `wk-${String(id || 'week').replace(/[^a-z0-9-]/gi, '')}`;
  const holdUntil = FIRST + (events.length - 1) * STEP + LAND + HOLD;
  const css = events.map((_, i) => {
    const start = FIRST + i * STEP;
    return `@keyframes ${ns}-${i}{0%,${pct(start)}%{opacity:0;transform:translateY(-10px) scale(.96)}`
      + `${pct(start + LAND)}%,${pct(holdUntil)}%{opacity:1;transform:none}${pct(holdUntil + CLEAR)}%,100%{opacity:0;transform:none}}`
      + `.${ns}-e${i}{animation:${ns}-${i} ${period.toFixed(2)}s cubic-bezier(.2,.8,.2,1) infinite}`;
  }).join('\n')
    + `\n.${ns}-ev{transform-box:fill-box;transform-origin:50% 0;opacity:0}`
    + `\n@media (prefers-reduced-motion:reduce){.${ns}-ev{animation:none!important;opacity:1;transform:none}}`;
  // Sized to its words: 11px bold runs about 6.7 units a character, with the dot and the padding beside.
  const pill = { w: Math.min(CARD.w - 180, 44 + (12 + from.length) * 6.7), h: 28 };
  const said = events.map((e) => `${e.title}, ${days[e.d].name.toLowerCase()} at ${clock(e.from)}`).join('; ');
  return (
    <svg className="site-week-art" viewBox={`0 0 ${W} ${H}`} role="img" aria-label={`${label}${from ? `, synced from ${from}` : ''}: ${said}`}>
      <style>{css}</style>
      <defs>
        <filter id={`${ns}-sh`} x="-10%" y="-10%" width="120%" height="130%">
          <feDropShadow dx="0" dy="10" stdDeviation="14" floodColor="#120a1c" floodOpacity=".35" />
        </filter>
      </defs>
      <rect x={CARD.x} y={CARD.y} width={CARD.w} height={CARD.h} rx={CARD.r} fill="#fff" filter={`url(#${ns}-sh)`} />
      <text x={CARD.x + 24} y={CARD.y + 40} className="site-week-h">{label}</text>
      {from && (
        <g>
          <rect x={CARD.x + CARD.w - 18 - pill.w} y={CARD.y + 18} width={pill.w} height={pill.h} rx={pill.h / 2} className="site-week-pill" />
          <circle cx={CARD.x + CARD.w - 18 - pill.w + 16} cy={CARD.y + 18 + pill.h / 2} r="5" className="site-week-dot" />
          <text x={CARD.x + CARD.w - 18 - pill.w + 28} y={CARD.y + 36.5} className="site-week-pilltext">{`Synced from ${from}`}</text>
        </g>
      )}
      {days.map((d, i) => {
        const cx = colX(i) + col / 2;
        return (
          <g key={d.key}>
            <text x={cx} y={CARD.y + 78} textAnchor="middle" className="site-week-s site-week-m">{d.name}</text>
            {d.today && <circle cx={cx} cy={CARD.y + 99} r="15" className="site-week-today" />}
            <text x={cx} y={CARD.y + 104.5} textAnchor="middle" className={`site-week-n${d.today ? ' is-today' : ''}`}>{d.num}</text>
            {i > 0 && <line x1={colX(i)} x2={colX(i)} y1={TOP - 8} y2={BOTTOM + 8} className="site-week-col" />}
          </g>
        );
      })}
      {hours.map((h) => (
        <g key={h}>
          <line x1={CARD.x + GUTTER - 6} x2={CARD.x + CARD.w - 18} y1={hourY(h)} y2={hourY(h)} className="site-week-line" />
          <text x={CARD.x + GUTTER - 14} y={hourY(h) + 4} textAnchor="end" className="site-week-s site-week-m">{hourLabel(h)}</text>
        </g>
      ))}
      {events.map((e, i) => {
        const x = colX(e.d) + 4, y = hourY(e.from) + 2, w = col - 8, h = Math.max(24, (e.to - e.from) * row - 4);
        // Two lines of title when the block is tall enough to keep its time beneath them.
        const title = wrap(e.title, room, h >= 62 ? 2 : 1);
        const timeY = 17 + title.length * 15;
        return (
          <g key={e.id} className={`${ns}-ev ${ns}-e${i}`}>
            <clipPath id={`${ns}-c${i}`}><rect x={x} y={y} width={w - 6} height={h} /></clipPath>
            <rect x={x} y={y} width={w} height={h} rx="7" className="site-week-ev" />
            <rect x={x} y={y} width="4" height={h} rx="2" className="site-week-bar" />
            {/* The words stay inside their block, however wide a title's capitals run. */}
            <g clipPath={`url(#${ns}-c${i})`}>
              {title.map((t, k) => <text key={k} x={x + 11} y={y + 17 + k * 15} className="site-week-b">{t}</text>)}
              {h > timeY + 4 && <text x={x + 11} y={y + timeY} className="site-week-s">{`${clock(e.from)}–${clock(e.to)} ${e.to >= 12 ? 'PM' : 'AM'}`}</text>}
              {h > timeY + 20 && e.style && <text x={x + 11} y={y + timeY + 15} className="site-week-s site-week-a">{cut(e.style, room + 12)}</text>}
            </g>
          </g>
        );
      })}
    </svg>
  );
}
